<template>
  <panel :title="title" v-mask="maskOptions">
    <template #icons>
      <panel-header-icon icon="sync" @click="refresh" />
    </template>
    <div class="chart-container">
      <chart-bar
        :options="chartOptions"
        :data="chartData"
      />
    </div>
    <div class="pay-period-grid">
      <!-- Total Hours Row -->
      <div class="grid-row">
        <div class="summary-cell">
          <span class="value-highlight" v-if="totalHours">{{ round(totalHours) }}</span>
          <span class="label-text">Total Employee Hours</span>
        </div>
        <div class="action-cell">
          <router-link :to="reviewHoursLink" class="btn-link">review</router-link>
        </div>
        <div class="action-cell">
          <router-link :to="printHoursLink" class="btn-link">print report</router-link>
        </div>
      </div>
      <!-- Exception Row -->
      <div class="grid-row">
        <div class="summary-cell">
          <font-awesome-icon icon="triangle-exclamation" class="icon text-amber-500" />
          <span class="value-highlight" v-if="exceptionCount">{{ exceptionCount }}</span>
          <span class="label-text">{{ inflect('Exception', exceptionCount) }}</span>
        </div>
        <div class="action-cell">
          <router-link :to="reviewExceptionsLink" class="btn-link">review</router-link>
        </div>
        <div class="action-cell">
          <router-link :to="printExceptionsLink" class="btn-link">print report</router-link>
        </div>
      </div>
      <!-- Failures Row -->
      <div class="grid-row" v-if="showClockLogFailures">
        <div class="summary-cell">
          <font-awesome-icon icon="triangle-exclamation" class="icon text-rose-500" />
          <span class="value-highlight" v-if="clockLogFailureCount">{{ clockLogFailureCount }}</span>
          <span class="label-text">{{ inflect('', clockLogFailureCount, 'Failure Requires', 'Failures Require') }} Action</span>
        </div>
        <div class="action-cell" v-if="canManageClockLogs">
          <router-link :to="reviewFailuresLink" class="btn-link">review</router-link>
        </div>
        <div class="action-cell" v-else></div>
      </div>
    </div>
  </panel>
</template>
<script>
import Panel from '@/components/Panel.vue'
import PanelHeaderIcon from '@/components/PanelHeaderIcon.vue'
import ChartBar from '@/components/ChartBar.vue'
import AutoRefresh from '@/mixins/AutoRefresh'
import Maskable from '@/mixins/Maskable'
import { mapGetters, mapState } from 'vuex'
import moment from 'moment-timezone'
import MomentFormats from '@/utils/MomentFormats'
import service from './services/PayPeriodService'
import { inflect } from 'inflection'
import _  from 'lodash'

const dateFormat = 'MMM DD'
const maxTicksLimit = 8

function getStepSize (periodDays) {
  if (periodDays <= 7) {
    return 1
  } else if (periodDays < 15) {
    return 2
  } else if (periodDays < 21) {
    return 3
  } else if (periodDays < 36) {
    return 7
  } else {
    return Math.ceil(periodDays / maxTicksLimit)
  }
}

export default {
  name: 'PayPeriod',
  mixins: [AutoRefresh, Maskable],
  components: {
    ChartBar,
    Panel,
    PanelHeaderIcon,
  },
  props: {
    title: {
      type: String,
      required: true
    },
    whichPeriod: {
      type: String,
      required: true
    },
    showClockLogFailures: {
      type: Boolean,
      default: false
    },
    barBackgroundColor: {
      type: String,
      required: true
    },
    barBorderColor: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapState(['timezone', 'payPeriodType', 'payPeriodStarts']),
    ...mapState('size', ['clientWidth', 'clientHeight']),
    ...mapGetters(['canManageClockLogs']),
    ...mapGetters({ getPayPeriodDateRange: 'payPeriodDateRange' }),
    payPeriodDateRange () {
      // Since payPeriodDateRange uses method-style access, and Vuex does not
      // react to changes to its dependencies, thus we need to declare the reactive
      // dependencies here.
      const _payPeriodType = this.payPeriodType
      const _payPeriodStarts = this.payPeriodStarts
      return this.getPayPeriodDateRange(this.whichPeriod, this.now)
    },
    formattedPayPeriodStartDate () {
      return moment(this.payPeriodDateRange[0]).format(MomentFormats.Iso8601Short)
    },
    formattedPayPeriodEndDate () {
      return moment(this.payPeriodDateRange[1]).subtract(1, 'day').format(MomentFormats.Iso8601Short)
    },
    periodDuration () {
      return moment.duration(this.payPeriodDateRange[1].diff(this.payPeriodDateRange[0]))
    },
    chartOptions () {
      const periodStart = this.payPeriodDateRange[0]
      const stepSize = getStepSize(this.periodDuration.asDays())

      return {
        indexAxis: 'y',
        scales: {
          x: {
            stacked: true,
            grid: {
              display: false
            },
            ticks: {
              color: () => document.documentElement.classList.contains('app-dark') ? '#A9BADC' : '#666666',
              stepSize,
              callback: (label, index, labels) => {
                if (index < labels.length - 1) {
                  return moment(periodStart).add(label, 'days').format(dateFormat)
                }
              }
            }
          },
          y: {
            stacked: true,
            display: false,
            grid: {
              display: false
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true
          }
        },
        responsive: true,
        maintainAspectRatio: false
      }
    },
    chartData () {
      const now = this.now
      const periodDays = this.periodDuration.asDays()
      const daysPast = this.payPeriodDateRange[1].isBefore(now)
        ? periodDays
        : moment.duration(now.diff(this.payPeriodDateRange[0])).asDays()

      const isDark = document.documentElement.classList.contains('app-dark')
      const futureBgColor = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
      const futureBorderColor = isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'

      return {
        labels: [''],
        datasets: [{
          label: 'Past',
          backgroundColor: this.barBackgroundColor,
          borderColor: this.barBorderColor,
          borderWidth: 1,
          borderRadius: 6,
          borderSkipped: false,
          data: [this.round(daysPast)]
        }, {
          label: 'Future',
          backgroundColor: futureBgColor,
          borderColor: futureBorderColor,
          borderWidth: 1,
          borderRadius: 6,
          borderSkipped: false,
          data: [this.round(periodDays - daysPast)]
        }]
      }
    },
    reviewHoursLink () {
      return {
        name: 'punch-list',
        params: {
          view: JSON.stringify({
            from: this.formattedPayPeriodStartDate,
            to: this.formattedPayPeriodEndDate,
            preset: this.whichPeriod
          })
        }
      }
    },
    printHoursLink () {
      return {
        name: 'timecards-report',
        params: {
          view: JSON.stringify({
            commondates: this.whichPeriod,
            group: ['employeeName']
          })
        }
      }
    },
    reviewExceptionsLink () {
      return {
        name: 'punch-list',
        params: {
          view: JSON.stringify({
            status: 'exception',
            from: this.formattedPayPeriodStartDate,
            to: this.formattedPayPeriodEndDate,
            preset: this.whichPeriod
          })
        }
      }
    },
    printExceptionsLink () {
      return {
        name: 'raw-punches-report',
        params: {
          view: JSON.stringify({
            status: 'exception',
            commondates: this.whichPeriod,
            group: ['employeeName']
          })
        }
      }
    },
    reviewFailuresLink () {
      return {
        name: 'clock-log-list',
        params: {
          view: JSON.stringify({
            status: 'requires_action',
            from: this.formattedPayPeriodStartDate,
            to: this.formattedPayPeriodEndDate,
            preset: this.whichPeriod
          })
        }
      }
    }
  },
  data () {
    return {
      // even though rest endpoint returns startDate and endDate,
      // we'll use locally calculated dates so that we can display
      // something even if rest call fails.
      totalHours: 0,
      exceptionCount: 0,
      clockLogFailureCount: 0,
      // now will be updated each time we load data in order to refresh chart
      now: moment()
    }
  },
  watch: {
    payPeriodType () {
      this.load()
    },
    payPeriodStarts () {
      this.load()
    }
  },
  methods: {
    load () {
      this.showLoadingMask()

      this.now = moment.tz(this.timezone)

      return service
        .retrievePayPeriodActivity(
          this.payPeriodDateRange[0].format(MomentFormats.Iso8601Short),
          // backend expects end of range to be inclusive
          moment(this.payPeriodDateRange[1]).subtract(1, 'days').format(MomentFormats.Iso8601Short),
          this.showClockLogFailures)
        .then(payPeriodActivity => {
          Object.assign(this.$data, payPeriodActivity || {})
          this.hideMask()
        })
        .catch(error => {
          this.showErrorMask(error)
          return Promise.reject(error)
        })
    },
    refreshImpl () {
      return this.load()
    },
    refresh () {
      this.coordinateWithRefresh(() => this.load())
    },
    inflect (str, count, singular, plural) {
      return inflect(str, count, singular, plural)
    },
    round (value) {
      return _.round(value, 1)
    }
  }
}
</script>
<style scoped>
.chart-container {
  position: relative;
  width: 100%;
  height: 50px;
  margin-bottom: 0.25rem;
}

.pay-period-grid {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-top: 0.5rem;
  width: 100%;
}

.grid-row {
  display: grid;
  grid-template-columns: 7fr 2fr 3fr;
  align-items: center;
  padding: 0.4rem 0.65rem;
  background-color: var(--color-slate-955);
  border: 1px solid var(--color-slate-800);
  border-radius: 8px;
  gap: 0.35rem;
  transition: all 0.2s ease;
}

.grid-row:hover {
  background-color: var(--color-slate-850);
  border-color: var(--color-indigo-500);
}

.summary-cell {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--color-slate-100);
  font-size: 0.8rem;
}

.value-highlight {
  font-weight: bold;
  font-size: 0.85rem;
}

.label-text {
  color: var(--color-slate-300);
}

.icon {
  font-size: 0.85rem;
}

.text-amber-500 {
  color: #f59e0b;
}

.text-rose-500 {
  color: #f43f5e;
}

.action-cell {
  text-align: right;
  font-size: 0.75rem;
}

.btn-link {
  color: var(--color-indigo-500);
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s ease;
}

.btn-link:hover {
  color: var(--color-indigo-400);
  text-decoration: underline;
}

@media only screen and (max-width: 400px) {
  .grid-row {
    grid-template-columns: 1fr;
    text-align: left;
    padding: 0.5rem;
    gap: 0.15rem;
  }
  .action-cell {
    text-align: left;
    padding-left: 0;
    margin-top: 0.15rem;
  }
}
</style>

